<template>
  <div
    v-if="showTiles"
    :class="{ 'border-b border-gray-300': !IS_MOBILE_APP }"
    class="mt-auto pt-12 pb-12 md:pb-6 md:border-none"
  >
    <h2 class="text-20 lg:text-28 text-gray-900 font-semibold mb-4">
      {{ $t('transfer_comparison.more_ways_to_send_support') }}
    </h2>
    <div class="grid grid-cols-2 gap-2 lg:gap-6">
      <SimpleTile
        v-for="tile in data?.tiles.crossSell"
        :key="tile.id"
        :tile="tile"
        @track-click="trackTile(tile)"
      />
      <SimpleTile
        v-if="IS_MOBILE_APP"
        class="flex flex-col justify-center"
        @track-click="trackTile(monitoTile)"
        :tile="monitoTile"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { GQLCrossSellTiles, GQLCrossSellTilesVariables } from './generated/cross-sell-tiles'
import QUERY from './cross-sell-tiles.graphql'
import type { Corridor } from '@core/types/imt'
import { tileClick } from '@core/analytics/core-events/tile'

const IS_MOBILE_APP = __IS_MOBILE_APP__

const { $lang, $apollo, $isPreview, $t, $features, $track } = useNuxtApp()

const monitoTile = computed(() => ({
  id: 'monito-tile',
  title: $t('transfer_comparison.more_from_monito'),
  description: '',
  icon: null,
  optionalCtaLabel: null,
  url: '/hub',
}))

const logger = useLogger('cross-sell-tiles')

const ONE_HOUR_IN_SECONDS = 3600
const ONE_DAY_IN_SECONDS = 3600 * 24

const corridor = inject('corridor') as Corridor

const { data, error } = await useAsyncCachedData<GQLCrossSellTiles>(
  `crossSellTiles:/${[$lang, null, corridor.countryFrom.iso, corridor.countryTo.iso].join('/')}`,
  async () => {
    const { data, errors } = await $apollo.query<GQLCrossSellTiles, GQLCrossSellTilesVariables>({
      query: QUERY,
      variables: {
        locale: $lang,
        userSituation: null,
        countryLivingIn: corridor.countryFrom.iso || null,
        countryOrigin: corridor.countryTo.iso || null,
        preview: $isPreview.value,
      },
      context: {
        cache: {
          type: 'revalidate',
          maxAge: ONE_HOUR_IN_SECONDS,
          revalidate: ONE_DAY_IN_SECONDS,
        },
      },
    })

    if (errors) {
      throw createGraphQLError(errors, 'Failed to fetch cross sell tiles')
    }
    return data
  },
)

const hasError = ref(false)
watch(
  error,
  () => {
    if (error.value) {
      logger.error(error.value)
      hasError.value = true
    }
  },
  { immediate: true },
)

const showTiles = computed(() => {
  return (
    $features?.getValue('imt_cross_sell_section') === 'on' &&
    (data.value?.tiles.crossSell?.length ?? 0) > 0
  )
})

const tilesDisplayed = computed(() => {
  return data.value?.tiles.crossSell
    ? {
        crossSell: data.value.tiles.crossSell.map((tile) => tile.id),
      }
    : undefined
})
async function trackTile(tile: { id: string; title: string; url: string }) {
  await $track(
    tileClick({
      section: 'cross-sell',
      tileId: tile.id,
      tileTitle: tile.title,
      tileUrl: tile.url,
      tilesDisplayed: tilesDisplayed.value,
    }),
  )
}
</script>
